import { useEffect, useState, useCallback } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, MapPin, ShieldCheck, AlertCircle, Navigation, ChevronRight, User } from "lucide-react";
import { useSport, SPORT_META } from "@/context/SportContext";
import FollowButton from "@/components/FollowButton";

// A verified badge on the card front — the two things a player cares about
// most before they hand over cash: an in-date certification and an in-date
// insurance policy that admin has eyeballed.
const VerifiedRow = ({ ok, label }) => (
  <span
    className={`text-[10px] uppercase tracking-wider label-mono inline-flex items-center gap-1 ${ok ? "text-[#CCFF00]" : "text-[#525252]"}`}
  >
    <ShieldCheck className="w-3 h-3" /> {label}
  </span>
);

export default function CoachesDirectory() {
  const { sport } = useSport();
  const meta = SPORT_META[sport] || SPORT_META.pickleball;
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const ctl = useListControls(items, { name: (c) => c.name, initialSort: "default" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("sport", sport);
      if (regionId) params.set("region_id", regionId);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/coaches/directory?${params.toString()}`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load coaches");
    } finally { setLoading(false); }
  }, [sport, regionId, q]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/regions?active=true").then((r) => setRegions(r.data)).catch(() => setRegions([]));
  }, []);

  const guessMyRegion = () => {
    if (!navigator.geolocation) { toast.error("Your browser doesn't support geolocation"); return; }
    toast.info("Looking up your area…");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // No coord-to-region service wired yet — fall back to a manual pick
        // with a friendly nudge. Region-boundary geocoding is a P2 (see PRD).
        toast.info(`We got your GPS (${pos.coords.latitude.toFixed(2)}, ${pos.coords.longitude.toFixed(2)}). Pick your nearest area from the dropdown.`);
      },
      () => toast.error("Could not read your location — pick a region manually"),
      { timeout: 6000 },
    );
  };

  return (
    <div className="space-y-6" data-testid="coaches-directory">
      <div className="fade-up">
        <div className="label-mono" style={{ color: meta.color }}>FIND A {meta.label.toUpperCase()} COACH</div>
        <h1 className="h-display text-4xl md:text-5xl tracking-tighter mt-1">Verified coaches near you.</h1>
        <div className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Every coach listed here has been through Rally Lab&apos;s admin review — certifications and insurance are eyeballed manually.
          Pick a region or type a name to browse.
        </div>
      </div>

      {/* Filters */}
      <div className="card-surface p-4 grid md:grid-cols-12 gap-3 items-end fade-up d1">
        <div className="md:col-span-5">
          <div className="label-mono mb-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Region</div>
          <div className="flex items-center gap-2">
            <select
              value={regionId}
              onChange={(e) => setRegionId(e.target.value)}
              data-testid="coaches-region"
              className="flex-1 bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none"
            >
              <option value="">Any region</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={guessMyRegion}
              data-testid="coaches-gps"
              title="Use GPS"
              className="border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-3 py-2.5 text-xs uppercase tracking-wider label-mono inline-flex items-center gap-1 transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" /> GPS
            </button>
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="label-mono mb-2 inline-flex items-center gap-1"><Search className="w-3 h-3" /> Coach name</div>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="coaches-search"
            placeholder="Type a name…"
            className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none"
          />
        </div>
        <div className="md:col-span-2">
          <div className="label-mono mb-2">&nbsp;</div>
          <div className="text-xs text-[#A3A3A3] label-mono">{items.length} coach{items.length === 1 ? "" : "es"}</div>
        </div>
      </div>

      {/* Cards */}
      {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}
      {!loading && !items.length && (
        <div className="card-surface p-8 text-center" data-testid="coaches-empty">
          <User className="w-10 h-10 mx-auto text-[#A3A3A3]" strokeWidth={1.5} />
          <div className="h-display text-2xl mt-3">No coaches match those filters</div>
          <div className="text-sm text-[#A3A3A3] mt-2">Try clearing the region or search text.</div>
        </div>
      )}
      {items.length > 0 && <ListControlsBar ctl={ctl} hideSearch modes={["default", "az", "za"]} testid="coach-directory" />}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 fade-up d2">
        {ctl.list.map((c) => (
          <div
            key={c.id}
            data-testid={`coach-card-${c.id}`}
            className="card-surface p-5 hover:border-[#CCFF00] transition-colors group relative"
          >
            <Link to={`/coaches/${c.id}`} className="block">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold group-hover:text-[#CCFF00] transition-colors">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wider label-mono text-[#A3A3A3] mt-1">
                    {c.sports.join(" · ")}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#CCFF00]" />
              </div>
              {c.is_selectable ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  <VerifiedRow ok={c.certification_verified} label="Cert verified" />
                  <VerifiedRow ok={c.insurance_verified} label="Insurance verified" />
                </div>
              ) : (
                <div className="text-[10px] uppercase tracking-wider label-mono inline-flex items-center gap-1 text-[#FF4500] mb-3">
                  <AlertCircle className="w-3 h-3" /> {c.reason}
                </div>
              )}
              {c.certification_body && (
                <div className="text-xs text-[#A3A3A3] line-clamp-1">Trained with <strong className="text-white">{c.certification_body}</strong></div>
              )}
              {c.regions.length > 0 && (
                <div className="text-[10px] uppercase tracking-wider label-mono text-[#525252] mt-2 flex flex-wrap gap-1">
                  {c.regions.slice(0, 3).map((r) => (
                    <span key={r.id} className="px-1.5 py-0.5 border border-[#262626]">{r.label}</span>
                  ))}
                </div>
              )}
            </Link>
            <div className="mt-3 pt-3 border-t border-[#141414] flex justify-end">
              <FollowButton coachId={c.id} size="sm" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
